import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../../global/api";
import Breadcrumbs from "../../components/others/BreadCrumbs";
import GlobalStyles from "../../global/GlobalStyles";
import ShippingDetailsCard from "../../components/others/ShippingDetailsCard";
import logo from "../../logo.png";
import toast from "react-hot-toast";
import BigLoader from "../../components/loaders/BigLoader";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import Geocoder from "leaflet-control-geocoder";
import "leaflet-control-geocoder/dist/Control.Geocoder.css";

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const ShippingDetails = () => {
  const { tracking_id } = useParams();
  const [shippingPackage, setShippingPackage] = useState({});
  const [loading, setLoading] = useState(false);
  const [location, setLocation] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [status, setStatus] = useState("");
  const [note, setNote] = useState("");
  const [showMap, setShowMap] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  const mapInstance = useRef(null);
  const markerInstance = useRef(null);
  const mapContainer = useRef(null);

  const defaultLocation = { lat: 40.7128, lng: -74.006 };

  const breadcrumbItems = [
    { name: "Dashboard", path: "/admin/dashboard" },
    { name: "Manage shipping", path: "/admin/dashboard/manage-shipping" },
    {
      name: "Shipment",
      path: `/admin/dashboard/manage-shipping/${tracking_id}`,
    },
  ];

  const initializeMap = () => {
    if (!mapContainer.current || mapContainer.current._leaflet_map) return;

    const map = L.map(mapContainer.current).setView(
      [defaultLocation.lat, defaultLocation.lng],
      13
    );

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map);

    // Add search control
    const geocoder = new Geocoder({
      defaultMarkGeocode: false,
      geocoder: new Geocoder.nominatim(),
      position: "topright",
    });

    geocoder.on("markgeocode", function (e) {
      const { center, name } = e.geocode;
      map.setView(center, 13);
      updateMarker(center.lat, center.lng, name);
    });

    geocoder.addTo(map);

    map.on("click", handleMapClick);
    mapInstance.current = map;
  };

  const cleanupMap = () => {
    if (mapInstance.current) {
      mapInstance.current.off();
      mapInstance.current.remove();
      mapInstance.current = null;
    }
    markerInstance.current = null;
  };

  const updateMarker = (lat, lng, address) => {
    if (mapInstance.current) {
      if (markerInstance.current) {
        mapInstance.current.removeLayer(markerInstance.current);
      }
      const marker = L.marker([lat, lng]).addTo(mapInstance.current);
      if (address) {
        marker.bindPopup(address).openPopup();
      }
      markerInstance.current = marker;
    }
  };

  const handleMapClick = async (e) => {
    const { lat, lng } = e.latlng;

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
      );
      const data = await response.json();

      const address = data.display_name || "Selected Location";

      setLocation(address);
      setLatitude(lat.toString());
      setLongitude(lng.toString());
      updateMarker(lat, lng, address);
      setShowMap(false);
    } catch (error) {
      console.error("Reverse geocoding error:", error);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          searchQuery
        )}`
      );
      const data = await response.json();

      if (data?.length > 0) {
        const { lat, lon, display_name } = data[0];
        updateMarker(lat, lon, display_name);
        if (mapInstance.current) {
          mapInstance.current.setView([lat, lon], 13);
        }
      }
    } catch (error) {
      console.error("Search error:", error);
      toast.error("Failed to find location");
    }
  };

  useEffect(() => {
    if (showMap) {
      initializeMap();
    } else {
      cleanupMap();
    }

    return () => {
      cleanupMap();
    };
    // eslint-disable-next-line
  }, [showMap]);

  const getShippingPackage = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/track/${tracking_id}`);
      setShippingPackage(response.data);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to load shipment details"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getShippingPackage();
    // eslint-disable-next-line
  }, []);

  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/shipments/${tracking_id}/tracking-update`, {
        location,
        latitude,
        longitude,
        status,
        notes: note,
      });
      getShippingPackage();
      setLocation("");
      setLatitude("");
      setLongitude("");
      setStatus("");
      setNote("");
      toast.success("Shipment updated successfully");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update shipment");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await api.delete(`/shipments/${tracking_id}`);
      navigate(-1);
      toast.success("Shipment deleted successfully");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete shipment");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={GlobalStyles.dashbooardPageContainer}>
      <div className={GlobalStyles.dashbooardPageWrap}>
        <div className={GlobalStyles.seperator}>
          <Breadcrumbs items={breadcrumbItems} />
        </div>
        {loading && (
          <div className="w-full flex justify-center py-40">
            <BigLoader />
          </div>
        )}
        {shippingPackage && !loading && (
          <>
            {showMap && (
              <div className="fixed inset-0 bg-black/90 z-50 flex flex-col items-center justify-center p-4">
                <div className="w-full max-w-4xl h-3/4 bg-white rounded-lg overflow-hidden relative">
                  <div
                    ref={mapContainer}
                    className="w-full h-full"
                    style={{ minHeight: "500px" }}
                  ></div>
                  <div className="absolute top-4 left-4 right-4 flex gap-2">
                    <form onSubmit={handleSearch} className="flex-1 flex gap-2">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search for a location..."
                        className="flex-1 p-2 rounded shadow-lg"
                      />
                      <button
                        type="submit"
                        className="bg-primary text-white px-4 py-2 rounded"
                      >
                        Search
                      </button>
                    </form>
                    <button
                      onClick={() => setShowMap(false)}
                      className="bg-white p-2 rounded shadow-lg"
                    >
                      Close
                    </button>
                  </div>
                  <div className="absolute bottom-4 left-0 right-0 text-center text-white bg-black/50 p-2">
                    {searchQuery
                      ? "Press enter to search or click on the map to select location"
                      : "Click on the map to select location"}
                  </div>
                </div>
              </div>
            )}

            <div className={GlobalStyles.sectionDivider}>
              <ShippingDetailsCard
                shippingPackage={shippingPackage}
                admin={true}
                getShippingPackage={getShippingPackage}
              />
              <div className={GlobalStyles.sectionWrap}>
                <div className="flex flex-col justify-center gap-5 lg:gap-10">
                  <form
                    className="w-full flex flex-col gap-5"
                    onSubmit={handleUpdate}
                  >
                    <div className="flex items-center gap-2">
                      <img
                        src={logo}
                        alt=""
                        className={GlobalStyles.formLogo}
                      />
                      <h2 className={GlobalStyles.formHeader}>
                        Add tracking update
                      </h2>
                    </div>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Location"
                        className={GlobalStyles.input}
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={() => setShowMap(true)}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-primary text-white px-2 py-1 rounded text-xs"
                      >
                        Map
                      </button>
                    </div>
                    <input
                      type="text"
                      placeholder="Note (optional)"
                      className={GlobalStyles.input}
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                    />
                    <select
                      className={GlobalStyles.input}
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                    >
                      <option value="">Select shipment status</option>
                      <option value="pending">Pending</option>
                      <option value="in_transit">In transit</option>
                      <option value="delivered">Delivered</option>
                    </select>
                    <button
                      type="submit"
                      className={GlobalStyles.buttonFull}
                      disabled={loading}
                    >
                      {loading ? "Updating..." : "Update"}
                    </button>
                  </form>
                  <div className="w-full flex flex-col gap-5">
                    <h4 className={GlobalStyles.sectionHeader}>
                      Update package
                    </h4>
                    <Link
                      to={`/admin/dashboard/manage-shipping/${tracking_id}/edit`}
                      className={GlobalStyles.buttonFull}
                    >
                      Update
                    </Link>
                  </div>
                  <div className="w-full flex flex-col gap-5">
                    <h4 className={GlobalStyles.sectionHeader}>
                      Delete package
                    </h4>
                    <button
                      onClick={handleDelete}
                      className={GlobalStyles.buttonFull}
                      disabled={loading}
                    >
                      {loading ? "Deleting..." : "Delete"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ShippingDetails;
